import React, { useState, useMemo } from 'react';
import { 
  Box, 
  IconButton, 
  Drawer, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Collapse,
  Divider,
  CircularProgress
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  SkipNext as SkipNextIcon,
  ExpandLess,
  ExpandMore,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  Undo as UndoIcon,
  Redo as RedoIcon
} from '@mui/icons-material';
import useCentralDiscoveryQueue from '../hooks/useCentralDiscoveryQueue';

const DiscoveryProgressIndicator = ({ 
  sessionId, 
  currentDiscoveryState,
  updateDiscoveryState, // Function to update current discovery topic
  sx = {} 
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedTopics, setExpandedTopics] = useState({});
  
  // Get queue data
  const { queueItems, progressPercentage, topicStats, markAsDiscovered } = useCentralDiscoveryQueue(sessionId);
  console.log('currentDiscoveryState', currentDiscoveryState);
  console.log('queueItems', queueItems);
  
  // Calculate overall progress
  const progressData = useMemo(() => {
    if (!currentDiscoveryState?.discoveryOrder || !queueItems) {
      return { percentage: 0, completedTopics: 0, totalTopics: 0, topicProgress: {} };
    }
    
    const discoveryOrder = currentDiscoveryState.discoveryOrder;
    const topicProgress = {};
    let totalCompleted = 0;
    
    discoveryOrder.forEach(topic => {
      const topicItems = queueItems.filter(item => item.topic === topic);
      const completedItems = topicItems.filter(item => item.disco_done === true);
      const skippedItems = topicItems.filter(item => item.skipped === true);
      const pendingItems = topicItems.filter(item => !item.disco_done && !item.skipped);
      
      const isTopicComplete = pendingItems.length === 0 && topicItems.length > 0;
      if (isTopicComplete) totalCompleted++;
      
      topicProgress[topic] = {
        total: topicItems.length,
        completed: completedItems.length,
        skipped: skippedItems.length,
        pending: pendingItems.length,
        items: topicItems,
        isComplete: isTopicComplete
      };
    });
    
    const percentage = discoveryOrder.length > 0 ? Math.round((totalCompleted / discoveryOrder.length) * 100) : 0;
    
    return {
      percentage,
      completedTopics: totalCompleted,
      totalTopics: discoveryOrder.length,
      topicProgress
    };
  }, [queueItems, currentDiscoveryState]);
  
  // Get gradient color based on progress
  const getProgressColor = (percentage) => {
    if (percentage === 0) return '#E5E7EB';
    if (percentage <= 25) return '#EF4444'; // Red
    if (percentage <= 50) return '#F59E0B'; // Orange  
    if (percentage <= 75) return '#3B82F6'; // Blue (primary)
    return '#10B981'; // Green (success)
  };
  
  const progressColor = getProgressColor(progressData.percentage);
  
  // Toggle topic expansion
  const toggleTopic = (topic) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topic]: !prev[topic]
    }));
  };
  
  // Format topic name for display
  const formatTopicName = (topic) => {
    const topicNames = {
      'skills': 'Skills & Qualifications',
      'experience': 'Work Experience',
      'location': 'Location Requirements', 
      'title': 'Job Titles',
      'company': 'Company Experience',
      'company_stage': 'Company Stage',
      'education': 'Education Requirements',
      'candidate_preview': 'Candidate Preview'
    };
    return topicNames[topic] || topic.charAt(0).toUpperCase() + topic.slice(1);
  };
  
  // Handle topic restart - reset all items in topic to pending and switch to that topic
  const handleRestartTopic = async (topic) => {
    console.log('🔄 Restarting discovery for topic:', topic);
    
    try {
      // Get all items for this topic
      const topicItems = queueItems.filter(item => item.topic === topic);
      
      // Reset all items to pending state
      for (const item of topicItems) {
        await markAsDiscovered(item.queue_id, {
          disco_done: false,
          skipped: false,
          discovery_result: null,
          updated_at: Date.now()
        });
      }
      
      // Switch current discovery topic to this topic
      if (updateDiscoveryState) {
        updateDiscoveryState({
          ...currentDiscoveryState,
          currentDiscoTopic: topic
        });
      }
      
      console.log('✅ Successfully restarted topic:', topic);
      
      // Close the sidebar after action
      setSidebarOpen(false);
      
    } catch (error) {
      console.error('❌ Error restarting topic:', error);
    }
  };
  
  // Handle item unskip - reset single item from skipped to pending and switch to that topic
  const handleUnskipItem = async (item) => {
    console.log('🔄 Unskipping item:', item.section_name, 'from topic:', item.topic);
    
    try {
      // Reset the item to pending state
      await markAsDiscovered(item.queue_id, {
        disco_done: false,
        skipped: false,
        skipped_at: null,
        updated_at: Date.now()
      });
      
      // Switch current discovery topic to this item's topic to trigger rediscovery
      if (updateDiscoveryState && item.topic) {
        console.log('🎯 Switching discovery topic to:', item.topic);
        updateDiscoveryState({
          ...currentDiscoveryState,
          currentDiscoTopic: item.topic
        });
      }
      
      console.log('✅ Successfully unskipped item and switched to topic:', item.section_name, item.topic);
      
      // Close the sidebar after action to show the chat interface for rediscovery
      setSidebarOpen(false);
      
    } catch (error) {
      console.error('❌ Error unskipping item:', error);
    }
  };

  // Handle item redo - reset single completed item to pending and switch to that topic for rediscovery
  const handleRedoItem = async (item) => {
    console.log('🔄 Redoing discovery for item:', item.section_name, 'from topic:', item.topic);
    
    try {
      // Reset the item to pending state
      await markAsDiscovered(item.queue_id, {
        disco_done: false,
        skipped: false,
        discovery_result: null,
        updated_at: Date.now()
      });
      
      // Switch current discovery topic to this item's topic to trigger rediscovery
      if (updateDiscoveryState && item.topic) {
        console.log('🎯 Switching discovery topic to:', item.topic);
        updateDiscoveryState({
          ...currentDiscoveryState,
          currentDiscoTopic: item.topic
        });
      }
      
      console.log('✅ Successfully reset item to pending and switched to topic:', item.section_name, item.topic);
      
      // Close the sidebar after action to show the chat interface for rediscovery
      setSidebarOpen(false);
      
    } catch (error) {
      console.error('❌ Error redoing item discovery:', error);
    }
  };

  // Get icon for completion status
  const getStatusIcon = (item) => {
    if (item.disco_done) {
      return <CheckCircleIcon sx={{ color: '#10B981', fontSize: 20 }} />;
    }
    if (item.skipped) {
      return <SkipNextIcon sx={{ color: '#6B7280', fontSize: 20 }} />;
    }
    return <RadioButtonUncheckedIcon sx={{ color: '#D1D5DB', fontSize: 20 }} />;
  };
  
  return (
    <>
      {/* Circular Progress Button */}
      <IconButton
        onClick={() => setSidebarOpen(true)}
        sx={{
          position: 'relative',
          width: 40,
          height: 40,
          backgroundColor: '#FFFFFF',
          border: '2px solid #E5E7EB',
          borderRadius: '50%',
          '&:hover': {
            backgroundColor: '#F9FAFB',
            borderColor: progressColor,
          },
          ...sx
        }}
      >
        {/* Background Circle */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={36}
          thickness={3}
          sx={{
            color: '#E5E7EB',
            position: 'absolute',
            top: 1,
            left: 1,
          }}
        />
        
        {/* Progress Circle */}
        <CircularProgress
          variant="determinate"
          value={progressData.percentage}
          size={36}
          thickness={3}
          sx={{
            color: progressColor,
            position: 'absolute',
            top: 1,
            left: 1,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        
        {/* Progress Text */}
        <Typography
          variant="caption"
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            color: progressColor,
            position: 'absolute',
          }}
        >
          {progressData.percentage}%
        </Typography>
      </IconButton>
      
      {/* Progress Sidebar */}
      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            backgroundColor: '#FFFFFF',
            borderLeft: '1px solid #E5E7EB',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <TimelineIcon sx={{ color: progressColor, mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
              Discovery Progress
            </Typography>
          </Box>
          
          {/* Overall Progress */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#F9FAFB', borderRadius: 2 }}>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
              Overall Completion
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ flexGrow: 1, mr: 2 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: '#E5E7EB',
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${progressData.percentage}%`,
                      height: '100%',
                      backgroundColor: progressColor,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: progressColor }}>
                {progressData.percentage}%
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              {progressData.completedTopics} of {progressData.totalTopics} topics complete
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          {/* Topics List */}
          <List sx={{ p: 0 }}>
            {currentDiscoveryState?.discoveryOrder?.map((topic) => {
              const topicData = progressData.topicProgress[topic];
              if (!topicData) return null;
              
              const isExpanded = expandedTopics[topic];
              const hasItems = topicData.items.length > 0;
              
              return (
                <React.Fragment key={topic}>
                  <ListItem
                    button={hasItems}
                    onClick={() => hasItems && toggleTopic(topic)}
                    sx={{
                      px: 0,
                      py: 1,
                      borderRadius: 1,
                      '&:hover': hasItems ? {
                        backgroundColor: '#F3F4F6'
                      } : {}
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {topicData.isComplete ? (
                        <CheckCircleIcon sx={{ color: '#10B981', fontSize: 24 }} />
                      ) : topicData.pending === 0 && topicData.skipped > 0 ? (
                        <SkipNextIcon sx={{ color: '#6B7280', fontSize: 24 }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ color: '#D1D5DB', fontSize: 24 }} />
                      )}
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={formatTopicName(topic)}
                      secondary={
                        hasItems ? 
                          `${topicData.completed} completed, ${topicData.skipped} skipped, ${topicData.pending} pending` :
                          'No items'
                      }
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: topicData.isComplete ? '#111827' : '#6B7280'
                      }}
                      secondaryTypographyProps={{
                        fontSize: '12px',
                        color: '#9CA3AF'
                      }}
                    />
                    
                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {/* Restart Topic Button - show if topic has completed or skipped items */}
                      {hasItems && (topicData.completed > 0 || topicData.skipped > 0) && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestartTopic(topic);
                          }}
                          sx={{
                            color: '#6B7280',
                            '&:hover': {
                              backgroundColor: '#F3F4F6',
                              color: '#374151'
                            }
                          }}
                          title="Restart Discovery"
                        >
                          <RefreshIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      
                      {/* Expand/Collapse Button */}
                      {hasItems && (
                        isExpanded ? <ExpandLess /> : <ExpandMore />
                      )}
                    </Box>
                  </ListItem>
                  
                  {/* Topic Items */}
                  {hasItems && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {topicData.items.map((item, index) => (
                          <ListItem key={item.queue_id || index} sx={{ pl: 4, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              {getStatusIcon(item)}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.section_name || `Item ${index + 1}`}
                              primaryTypographyProps={{
                                fontSize: '13px',
                                color: item.disco_done ? '#111827' : item.skipped ? '#6B7280' : '#9CA3AF'
                              }}
                            />
                            
                            {/* Unskip Button - show only for skipped items */}
                            {item.skipped && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnskipItem(item);
                                }}
                                sx={{
                                  color: '#6B7280',
                                  '&:hover': {
                                    backgroundColor: '#F3F4F6',
                                    color: '#374151'
                                  }
                                }}
                                title="Unskip Item"
                              >
                                <UndoIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                            
                            {/* Redo Button - show only for completed items */}
                            {item.disco_done && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRedoItem(item);
                                }}
                                sx={{
                                  color: '#6B7280',
                                  '&:hover': {
                                    backgroundColor: '#F3F4F6',
                                    color: '#374151'
                                  }
                                }}
                                title="Redo Discovery"
                              >
                                <RedoIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default DiscoveryProgressIndicator;